import React, {useCallback, useState, useEffect} from 'react';
import {useFormik} from 'formik';
import * as Yup from 'yup';
import {useAppSelector, useAppDispatch} from 'store/customer';
import {
    getRectangularElementDimension,
    updateDimension,
    getRoomDimensions,
    getTopViewZoom,
    setPreviewPosition,
    getRoomType,
    getLShapedElementDimension,
    getLayoutId,
    setIsEditingRoomDimension,
    setRoomType,
    setIsTopView,
    setLShapedDimension,
    setRectangularDimension,
    getLayoutData,
    setIsEditHeightView,
    setToInitialState,
    getOldCabinetsPosition,
    setHiddenWall,
} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {shallowEqual, useSelector} from 'react-redux';
import {
    CabinetMesh,
    CreateLayoutRequest,
    RoomType,
} from 'components/customer/RoomPlanner/types';
import {PreviewPosition} from 'components/customer/Preview3D/types';
import styled from 'styled-components';
import {useDebouncedCallback} from 'use-debounce';
import {CBCButton} from 'shared/helpers';
import {useParams} from 'react-router-dom';
import {
    useCreateLayoutMutation,
    useUpdateLayoutMutation,
} from 'components/customer/RoomPlanner/store/roomPlannerApi';
import {invalidateJob} from 'components/customer/Job/store/jobApi';
import {autoSave} from 'components/customer/RoomPlanner/lib/useAutoSave';
import {useRoomPlannerContext} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import {resetOriginalCabinetPosition} from 'components/customer/RoomPlanner/helpers/resetOriginalCabinetPosition';
import {setHiddenWallWithRetry} from 'components/customer/RoomPlanner/helpers/hiddenWallRetry';
import {
    mobileLandscape,
    mobilePortrait,
    tabletLandscape,
    tabletPortrait,
} from 'shared/Breakpoints';
import {useIsTouchDevices} from 'shared/helpers/DeviceSize';
import {FieldError} from 'components/customer/RoomPlanner/components/RoomDimensionOverlay';

const validationSchema = Yup.object().shape({
    height: Yup.number().min(1000, 'Minimum wall height is 1000mm'),
});

const ErrorMessage = styled(FieldError)`
    text-align: right;
`;

const RoomHeightOverlay = ({
    previewPositionRef,
}: {
    previewPositionRef: React.MutableRefObject<PreviewPosition>;
}) => {
    const dispatch = useAppDispatch();
    const [createLayout] = useCreateLayoutMutation();
    const [updateLayout] = useUpdateLayoutMutation();
    const rectangularElementDimension = useAppSelector(
        getRectangularElementDimension
    );
    const {rectangularDimension, lShapedDimension} = useAppSelector(
        getRoomDimensions,
        shallowEqual
    );
    const lShapedElementDimension = useAppSelector(getLShapedElementDimension);
    const topViewZoom = useAppSelector(getTopViewZoom);
    const roomType = useAppSelector(getRoomType);
    const [width, setWidth] = useState(0);
    const [length, setLength] = useState(0);
    const {roomId} = useParams();
    const layoutId = useAppSelector(getLayoutId);
    const layoutData = useAppSelector(getLayoutData);
    const {cabinetGroupListRef} = useRoomPlannerContext();
    const oldCabinets = useSelector(getOldCabinetsPosition);

    const isMobileDevice = useIsTouchDevices();

    useEffect(() => {
        if (rectangularElementDimension) {
            let width = rectangularElementDimension?.length;
            let length = rectangularElementDimension?.height;

            if (isMobileDevice) {
                const mainContent =
                    document.querySelector('.component-section');
                const dimension = mainContent.getBoundingClientRect();

                width = dimension.width;
                length = dimension.height;
            }

            setWidth(width * topViewZoom + 25);
            setLength(length * topViewZoom);
        }
    }, [rectangularElementDimension, topViewZoom]);

    useEffect(() => {
        if (lShapedElementDimension) {
            let rightWidth = lShapedElementDimension?.rightWidth;
            let leftWidth = lShapedElementDimension?.leftWidth;

            if (isMobileDevice) {
                const mainContent =
                    document.querySelector('.component-section');
                const dimension = mainContent.getBoundingClientRect();

                rightWidth = dimension.width;
                leftWidth = dimension.height;
            }

            setWidth(leftWidth * topViewZoom + 25);
            setLength(rightWidth * topViewZoom);
        }
    }, [lShapedElementDimension, topViewZoom, isMobileDevice]);

    useEffect(() => {
        dispatch(setHiddenWall(null));
    }, [dispatch]);

    const formik = useFormik({
        initialValues: {
            ...rectangularDimension,
            ...lShapedDimension,
            ...(roomType === 'RECTANGULAR'
                ? {
                      height: rectangularDimension.height,
                  }
                : {
                      height: lShapedDimension.height,
                  }),
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: undefined,
    });

    const hasError = (fieldName: keyof typeof formik.values) => {
        /* eslint-disable-next-line security/detect-object-injection --
         * Imposible to fix without using formik.touched and formik.errors
         **/
        return formik.touched[fieldName] && formik.errors[fieldName];
    };

    const updateDimensionDebounced = useDebouncedCallback(
        (fieldName: string, roomType: RoomType, value: number) => {
            dispatch(
                updateDimension({
                    field: fieldName,
                    value: value,
                    type: roomType,
                })
            );
        },
        1000
    );

    const handleFieldChange = useCallback(
        (fieldName: string, roomType: RoomType) =>
            (e: React.ChangeEvent<HTMLInputElement>) => {
                formik.handleChange(e);
                const value = Number(e.target.value);

                void validationSchema
                    .validateAt(fieldName, {
                        [fieldName]: value,
                    })
                    .then(() => {
                        // If validation passes, update the dimension
                        updateDimensionDebounced(fieldName, roomType, value);
                    })
                    .catch(() => {
                        // If validation fails, don't update the dimension
                    });
            },
        []
    );

    const handleConfirmHeight = useCallback(async () => {
        try {
            const payload: CreateLayoutRequest = {
                roomId: parseInt(roomId),
                layout_room_shape_id: roomType === 'RECTANGULAR' ? 1 : 2,
                room_dimensions:
                    roomType === 'LSHAPED'
                        ? {
                              leftWidth: lShapedDimension.leftWidth,
                              rightWidth: lShapedDimension.rightWidth,
                              leftDepth: lShapedDimension.leftDepth,
                              rightDepth: lShapedDimension.rightDepth,
                              height: lShapedDimension.height,
                          }
                        : {
                              width: rectangularDimension.width,
                              length: rectangularDimension.length,
                              height: rectangularDimension.height,
                          },
                status: 1,
                layout_grid_visible: 0,
                layout_grid_height: 0,
                layout_grid_width: 0,
            };
            if (layoutId) {
                await updateLayout(payload).unwrap();
            } else {
                await createLayout(payload).unwrap();
                dispatch(invalidateJob());
                // dispatch(setLayoutId(result.layout_room_id));
                // payload.id = result.layout_room_id;
                // payload.layout_room_products = [];
                // dispatch(
                //     setLayoutData(payload as unknown as RoomPlannerLayout)
                // );
            }

            dispatch(setPreviewPosition('RIGHT'));
            dispatch(setIsEditingRoomDimension(false));
        } catch (error) {}
    }, [dispatch, roomType, lShapedDimension, rectangularDimension, layoutId]);

    const getInputStyle = (fieldName: keyof typeof formik.values) => {
        return {
            border: hasError(fieldName) ? '1px solid red' : '1px solid #ccc',
            outline: 'none',
            '&:focus': {
                border: hasError(fieldName)
                    ? '1px solid red'
                    : '1px solid #ccc',
            },
        };
    };

    const getLabelStyle = (fieldName: keyof typeof formik.values) => {
        return {
            color: hasError(fieldName) ? 'red' : '#666',
        };
    };

    const getErrorMessage = () => {
        const errors = Object.entries(formik.errors).map(([, error]) => {
            return `${error}`;
        });
        return errors.join('\n');
    };

    const handleCancelShape = useCallback(() => {
        if (!layoutData) {
            dispatch(setToInitialState());
        } else {
            const layout = {...layoutData};
            const isRectangular = layout.layout_room_shape_id === 1;
            previewPositionRef.current = 'RIGHT';

            if (isRectangular) {
                dispatch(
                    setRectangularDimension({
                        width: layout.room_dimensions.width,
                        length: layout.room_dimensions.length,
                        height: layout.room_dimensions.height,
                    })
                );
            } else {
                dispatch(
                    setLShapedDimension({
                        leftWidth: layout.room_dimensions.leftWidth,
                        rightWidth: layout.room_dimensions.rightWidth,
                        leftDepth: layout.room_dimensions.leftDepth,
                        rightDepth: layout.room_dimensions.rightDepth,
                        height: layout.room_dimensions.height,
                    })
                );
            }
        }
        dispatch(setIsTopView(false));
        dispatch(setIsEditHeightView(false));
        dispatch(setIsEditingRoomDimension(false));
        dispatch(
            setRoomType(
                layoutData?.layout_room_shape_id === 1
                    ? 'RECTANGULAR'
                    : 'LSHAPED'
            )
        );
        setHiddenWallWithRetry(dispatch, 'rightWall, frontWall');
        resetOriginalCabinetPosition({
            cabinetGroupListRef,
            oldCabinets,
            autoSave,
        });
    }, [dispatch, layoutData]);

    const handleConfirmHeightCallback = useCallback(() => {
        void handleConfirmHeight();
        cabinetGroupListRef.current.forEach((cabinet: CabinetMesh) => {
            autoSave(cabinet, false, false);
        });
    }, [handleConfirmHeight]);

    return (
        <>
            {roomType === 'RECTANGULAR' ? (
                <Rectangle $width={width} $length={length}>
                    <LeftInputContainer>
                        <Label style={getLabelStyle('height')}>
                            Wall Height
                        </Label>
                        <InputWrapper>
                            <ArrowInput
                                type="text"
                                name="height"
                                value={formik.values.height}
                                onChange={handleFieldChange(
                                    'height',
                                    'RECTANGULAR'
                                )}
                                onBlur={formik.handleBlur}
                                style={getInputStyle('height')}
                            />
                            <InputSuffix>mm</InputSuffix>
                        </InputWrapper>
                        {Object.keys(formik.errors).length > 0 && (
                            <ErrorMessage>{getErrorMessage()}</ErrorMessage>
                        )}
                    </LeftInputContainer>
                </Rectangle>
            ) : null}

            {roomType === 'LSHAPED' ? (
                <Rectangle $width={width} $length={length}>
                    <LeftInputContainer>
                        <Label style={getLabelStyle('height')}>
                            Wall Height
                        </Label>
                        <InputWrapper>
                            <ArrowInput
                                type="text"
                                name="height"
                                value={formik.values.height}
                                onChange={handleFieldChange(
                                    'height',
                                    'LSHAPED'
                                )}
                                onBlur={formik.handleBlur}
                                style={getInputStyle('height')}
                            />
                            <InputSuffix>mm</InputSuffix>
                        </InputWrapper>
                        {Object.keys(formik.errors).length > 0 && (
                            <ErrorMessage>{getErrorMessage()}</ErrorMessage>
                        )}
                    </LeftInputContainer>
                </Rectangle>
            ) : null}

            {/* {Object.keys(formik.errors).length > 0 && (
                <ErrorMessage>{getErrorMessage()}</ErrorMessage>
            )} */}

            <ButtonContainer>
                <Button
                    onClick={handleCancelShape}
                    iconName="Button-Cancel.svg"
                    className="job-button button-light"
                    style={{color: '#8A8080'}}>
                    Cancel
                </Button>

                <CBCButton
                    type="button"
                    onClick={handleConfirmHeightCallback}
                    iconName="Button-Tick.svg"
                    className="job-button button-blue"
                    disabled={Object.keys(formik.errors).length > 0}>
                    Confirm Wall Height
                </CBCButton>
            </ButtonContainer>
        </>
    );
};

const Rectangle = styled.div<{$width: number; $length: number}>`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: ${(props) => props.$width}px;
    height: ${(props) => props.$length}px;
    margin-top: 30px;
    margin-left: -50px;

    ${mobilePortrait`
        transform: translate(-12%, -50%);
    `}
`;

const InputContainer = styled.div`
    position: absolute;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
`;

// InputContainer for left input (centered vertically on the left edge)
const LeftInputContainer = styled(InputContainer)`
    left: 0;
    top: 50%;
    transform: translateY(-50%) translateX(-100%);
`;

const Label = styled.span`
    font-size: 12px;
    color: #666;
    font-weight: 500;
`;

const InputWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

const ArrowInput = styled.input`
    width: 100px;
    text-align: center;
    padding: 4px 25px 4px 4px;
    border: 1px solid #ccc;
    border-radius: 4px;
`;

const InputSuffix = styled.span`
    position: absolute;
    right: 8px;
    color: #666;
    font-size: 12px;
    pointer-events: none;
`;

const ButtonContainer = styled.div`
    position: fixed;
    bottom: 52px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 100;
    display: flex;
    gap: 16px;

    ${mobilePortrait`
        bottom: 5px;
        left: 0;
        right: 0;
        transform: initial;
        padding: 0 15px;

        & > button {
            font-size: 0.8em !important;
            width: 80% !important;
        }    
    `}

    ${mobileLandscape`
        bottom: 10px;
        z-index: 999991;
        left: initial;
        right: 10px;
        transform: initial;
    `}
    
    ${tabletPortrait`
        bottom: 10px;
        z-index: 999991;
    `}

    ${tabletLandscape`
        bottom: 5px; 
        z-index: 999991;   
    `}
`;

// FIXME: Use CBCButton instead of inline component when we have
// a proper cypress test.
const Button = styled((props: React.ComponentProps<typeof CBCButton>) => (
    <CBCButton {...props} />
))`
    border-radius: 40px !important;
    padding: 5px 10px 5px 5px !important;
    width: fit-content !important;
    font-size: 0.95em !important;
    &:hover {
        border: 1px solid white;
    }
    @media (max-width: 695px) {
        font-size: 0.8em !important;
        width: 80% !important;
    }
`;

export default RoomHeightOverlay;
